/**
 * MOSA-364. One module turns every way the Sheets connect flow can fail into
 * what the user sees, the exact fix, and the buttons that carry it out.
 *
 * Nothing here invents an error: the input is either a typed API `code`, a
 * parser reason, or an HTTP status. Anything unrecognised becomes `unknown`,
 * which still shows plain copy and a retry, never a code or a stack trace.
 *
 * MOSA-468 adds the four rows the board table has and this module did not:
 * a Workspace admin block, a missing scope box, a blocked Picker window, and
 * the "Connected as <email>" line. Each row is a `SheetsFailureCase` below,
 * and `sheets-failure.test.ts` has a test per row plus the mapping table.
 */

export type SheetsFailureCase =
  /** Not a Google Sheets link, or an .xlsx in Drive. */
  | "invalid_link"
  /** Not signed in to OnlyDash. */
  | "not_signed_in"
  /** Google consent denied, or the window closed. */
  | "consent_denied"
  /** The signed-in Google account cannot open this file. */
  | "no_access"
  /** The Picker closed without granting this file. */
  | "picker_not_granted"
  /** Sheet deleted, trashed, or never existed. */
  | "sheet_not_found"
  /** Quota, rate limit or a Google 5xx. */
  | "temporary"
  /** Above SHEETS_MAX_CELLS. */
  | "too_large"
  /** No OAuth or Picker environment. */
  | "not_configured"
  /** Picker returned a different file than the pasted link (EM scope). */
  | "wrong_file"
  /**
   * Session PATCH with datasourceId failed: the datasource sheet is not the
   * session link (wizard_session_datasource_spreadsheet_mismatch).
   */
  | "spreadsheet_mismatch"
  /** 409 sheets_datasource_exists: same sheet under another Google account. */
  | "already_connected"
  /** MOSA-468. The Google consent screen left the Drive file box unticked. */
  | "scope_missing"
  /** MOSA-468. A Google Workspace admin policy blocks the app. */
  | "admin_policy"
  /** MOSA-468. The Picker window was blocked by a popup or cookie rule. */
  | "picker_blocked"
  /** Analyse poll timed out or the panel step stopped mid-way. */
  | "analyse_timeout"
  /**
   * MOSA-407. The Google email already has an OnlyDash account with a
   * password. One password sign-in links Google to it; OnlyDash never merges
   * the two by itself.
   */
  | "link_required"
  /**
   * MOSA-408. The link is a Google Doc, Form or Slides file. Each one has its
   * own fix, so they are three cases, not one "bad link".
   */
  | "google_doc"
  | "google_form"
  | "google_slides"
  /** MOSA-408. The link is a Drive folder: pick one sheet inside it. */
  | "drive_folder"
  /** MOSA-408. A "publish to the web" copy, not the sheet itself. */
  | "published_copy"
  /** MOSA-408. Drive says the file is an Excel or CSV upload, not a Sheet. */
  | "excel_file"
  /** MOSA-408. A write was tried on a panel connected read-only. */
  | "read_only_panel"
  /** No human-readable cause: still actionable. */
  | "unknown";

export type SheetsFailureAction =
  | "edit_link"
  | "sign_up"
  | "sign_in"
  | "try_again"
  | "switch_account"
  | "copy_request_message"
  | "open_request_access"
  | "open_picker"
  | "pick_pasted_sheet"
  | "use_picked_file"
  | "retry_later"
  /** Open the existing datasource from a 409 sheets_datasource_exists. */
  | "open_existing"
  /** Reconnect / switch Google so this account owns the sheet connection. */
  | "use_my_account"
  /** MOSA-468. Copy the request for the Google Workspace admin. */
  | "copy_admin_request"
  /** MOSA-468. Open the Picker in its own tab, which keeps the wizard session. */
  | "open_picker_new_tab"
  /** MOSA-407. Sign in with the password on this email, to link Google to it. */
  | "sign_in_to_link"
  /** MOSA-408. Copy the Excel file into a Google Sheet and read the copy. */
  | "convert_to_sheet"
  /** MOSA-408. Open the Picker inside the folder the user pasted. */
  | "pick_in_folder"
  /** MOSA-408. Connect the sheet with writes turned off. */
  | "connect_read_only"
  /** MOSA-408. Ask the sheet owner for edit access, not just for access. */
  | "request_edit_access"
  /** MOSA-408. Open the sheet so the user can type column names in row 1. */
  | "add_starter_headers"
  /** MOSA-408. Start from a Google Sheets template that already has names. */
  | "use_template";

export type SheetsFailure = {
  case: SheetsFailureCase;
  /** One sentence: what happened. */
  title: string;
  /** Plain words: why, or what is being paused. */
  explanation: string;
  /** The exact fix, in the user's language. */
  fix: string;
  /** Buttons, first is the primary action. */
  actions: SheetsFailureAction[];
  /** Quota and 5xx wait this long before the wizard retries by itself. */
  retryAfterSeconds?: number;
  /** Which Google account to name in the "no access" copy. */
  googleAccount?: string | null;
  /** The account the user is signed in to OnlyDash with, for diagnostics. */
  onlyDashAccount?: string | null;
  /** Existing datasource id from 409 sheets_datasource_exists. */
  existingDatasourceId?: string | null;
  /** MOSA-408. Drive file id from a 400 sheets_is_excel, for the convert step. */
  driveFileId?: string | null;
  /** MOSA-408. The file name Drive reported, so the copy can name the file. */
  driveFileName?: string | null;
  /** MOSA-408. Drive folder id from a 400 drive_folder_link, for the Picker. */
  folderId?: string | null;
};

const ACTION_LABEL: Record<SheetsFailureAction, string> = {
  edit_link: "Edit the link",
  sign_up: "Sign up",
  sign_in: "Sign in",
  try_again: "Try again",
  switch_account: "Switch Google account",
  copy_request_message: "Copy request-access message",
  open_request_access: "Open the sheet to request access",
  open_picker: "Open the picker again",
  pick_pasted_sheet: "Use the sheet from the link",
  use_picked_file: "Switch to the one you picked",
  retry_later: "Retry now",
  open_existing: "Open it",
  use_my_account: "Switch Google account",
  copy_admin_request: "Copy request for your Workspace admin",
  open_picker_new_tab: "Open the file window in a new tab",
  sign_in_to_link: "Sign in with your password",
  convert_to_sheet: "Convert to a Google Sheet",
  pick_in_folder: "Choose a sheet in this folder",
  connect_read_only: "Connect read-only",
  request_edit_access: "Ask the owner for edit access",
  add_starter_headers: "Open the sheet and add column names",
  use_template: "Use a template",
};

export const actionLabel = (action: SheetsFailureAction): string =>
  ACTION_LABEL[action];

/** Where the failed step lives, so the wizard can re-run only that step. */
export type SheetsFailureStage =
  | "link"
  | "account"
  | "consent"
  | "picker"
  | "read"
  | "panel";

type Build = {
  stage: SheetsFailureStage;
  googleAccount?: string | null;
  onlyDashAccount?: string | null;
};

/** The message a user copies to ask the sheet owner for access. */
export function requestAccessMessage(args: {
  googleAccount?: string | null;
  sheetUrl: string;
  onlyDashAccount?: string | null;
}): string {
  const who = args.googleAccount
    ? `the Google account ${args.googleAccount}`
    : "my Google account";
  const account = args.onlyDashAccount
    ? `\n\nI am signed in to OnlyDash as ${args.onlyDashAccount}.`
    : "";
  return `Hi. Can you share this Google Sheet with ${who} as a viewer or editor? OnlyDash reads it for my admin panel: ${args.sheetUrl}${account}`;
}

/**
 * MOSA-468. The message a user copies to ask a Google Workspace admin to allow
 * OnlyDash. It names the app and the scope, so the admin has one fact to check
 * and no link to trust from the user.
 */
export function workspaceAdminRequestMessage(args: {
  googleAccount?: string | null;
  onlyDashAccount?: string | null;
}): string {
  const who = args.googleAccount
    ? `I sign in with the Google account ${args.googleAccount}.`
    : "I sign in with my work Google account.";
  const account = args.onlyDashAccount
    ? ` My OnlyDash account is ${args.onlyDashAccount}.`
    : "";
  return `Hello. I cannot connect OnlyDash to Google Sheets. Google says that my Google Workspace blocks this app. Please allow the OnlyDash app for my account or for the Workspace. It asks for one scope only: https://www.googleapis.com/auth/drive.file. This scope gives access to the files that I pick. ${who}${account}`;
}

/** MOSA-468. The "Connected as <email> · Switch account" line. */
export function connectedAsLabel(email: string | null | undefined): string {
  return email ? `Connected as ${email}` : "Google is connected";
}

/**
 * How long to wait before the wizard retries a temporary failure by itself.
 * Google publishes a Retry-After header on 429; when we have it, use it.
 */
function retryAfterSeconds(error: unknown): number {
  const headers = (
    error as {
      response?: { headers?: Record<string, string | undefined> };
    }
  )?.response?.headers;
  const header = headers?.["retry-after"] ?? headers?.["Retry-After"];
  const parsed = header ? Number(header) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.min(Math.ceil(parsed), 120);
  }
  return 10;
}

function httpStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return typeof status === "number" ? status : undefined;
}

/** The API's typed `code`, wherever the caller caught the error. */
export function apiErrorCode(error: unknown): string | undefined {
  const data = (
    error as { response?: { data?: { code?: unknown } } }
  )?.response?.data;
  if (data && typeof data.code === "string") {
    return data.code;
  }
  // A code we produced ourselves (parser, picker) rather than axios.
  const own = (error as { code?: unknown })?.code;
  return typeof own === "string" ? own : undefined;
}

const BY_CODE: Record<string, SheetsFailureCase> = {
  // parser and link checks
  not_a_sheets_link: "invalid_link",
  excel_link: "excel_file",
  missing_id: "invalid_link",
  // MOSA-408. The parser and the probe name the link type, so the wizard can
  // give the one fix that applies instead of "that is not a sheet link".
  google_doc_link: "google_doc",
  google_form_link: "google_form",
  google_slides_link: "google_slides",
  drive_folder_link: "drive_folder",
  published_copy_link: "published_copy",
  sheets_is_excel: "excel_file",
  datasource_read_only: "read_only_panel",
  // account
  signup_required: "not_signed_in",
  // Google connection
  google_reconnect_required: "consent_denied",
  google_connection_not_found: "consent_denied",
  google_signin_required: "consent_denied",
  // the one file
  google_file_access_lost: "no_access",
  // MOSA-468. The consent callback, both from our token exchange and from
  // Google's own redirect error.
  google_scope_missing: "scope_missing",
  google_admin_policy_enforced: "admin_policy",
  org_internal: "admin_policy",
  admin_policy_enforced: "admin_policy",
  // picker
  picker_not_confirmed: "picker_not_granted",
  picker_wrong_file: "wrong_file",
  picker_blocked: "picker_blocked",
  wizard_session_datasource_spreadsheet_mismatch: "spreadsheet_mismatch",
  sheets_datasource_exists: "already_connected",
  // the file itself
  sheet_not_found: "sheet_not_found",
  // size
  sheets_too_large: "too_large",
  // configuration
  google_not_configured: "not_configured",
};

function caseFromStatus(status: number | undefined): SheetsFailureCase | null {
  if (status == null) return null;
  if (status === 401) return "not_signed_in";
  if (status === 403) return "no_access";
  if (status === 404) return "sheet_not_found";
  // Bare 409 without a typed code: Google reconnect. Datasource conflicts use
  // sheets_datasource_exists and map via BY_CODE.
  if (status === 409) return "consent_denied";
  if (status === 413) return "too_large";
  if (status === 429) return "temporary";
  if (status === 503) return "not_configured";
  if (status >= 500) return "temporary";
  return null;
}

/**
 * Map an error (axios rejection, parser result, Picker callback) to a case and
 * its copy. `stage` decides which of two overlapping cases wins: a 403 during
 * the read is "no access", the same 403 during sign-in is "consent denied".
 */
function existingDatasourceIdFromError(error: unknown): string | null {
  const data = (
    error as { response?: { data?: Record<string, unknown> }; existingId?: unknown }
  )?.response?.data;
  if (data && typeof data === "object") {
    const id = data.id ?? data.datasourceId ?? data.existingId;
    if (typeof id === "string" && id.trim()) return id.trim();
  }
  const own = (error as { existingId?: unknown })?.existingId;
  return typeof own === "string" && own.trim() ? own.trim() : null;
}

/**
 * MOSA-408. The fields a probe refusal carries alongside its code: the Drive
 * file to convert, and the folder to open the Picker in.
 */
function driveDetailsFromError(error: unknown): {
  driveFileId: string | null;
  driveFileName: string | null;
  folderId: string | null;
} {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  const text = (value: unknown): string | null =>
    typeof value === "string" && value.trim() ? value.trim() : null;
  return {
    driveFileId: text(data?.fileId),
    driveFileName: text(data?.fileName),
    folderId: text(data?.folderId),
  };
}

export function sheetsFailureFromError(
  error: unknown,
  build: Build,
): SheetsFailure {
  const code = apiErrorCode(error);
  const mapped = (code && BY_CODE[code]) || caseFromStatus(httpStatus(error));
  const failureCase =
    mapped === "consent_denied" && build.stage === "read"
      ? ("no_access" as SheetsFailureCase)
      : mapped === "no_access" && build.stage === "consent"
        ? ("consent_denied" as SheetsFailureCase)
        : mapped ?? "unknown";
  return failureFailureCopy(failureCase, error, {
    ...build,
    existingDatasourceId: existingDatasourceIdFromError(error),
    ...driveDetailsFromError(error),
  });
}

type Extra = {
  sheetUrl?: string;
  existingDatasourceId?: string | null;
  driveFileId?: string | null;
  driveFileName?: string | null;
  folderId?: string | null;
};

/** A failure the wizard detects itself, with no error object. */
export function sheetsFailure(
  failureCase: SheetsFailureCase,
  build: Build,
  extra: Extra = {},
): SheetsFailure {
  return failureFailureCopy(failureCase, null, { ...build, ...extra });
}

function failureFailureCopy(
  failureCase: SheetsFailureCase,
  error: unknown,
  build: Build & Extra,
): SheetsFailure {
  const googleAccount = build.googleAccount ?? null;
  const onlyDashAccount = build.onlyDashAccount ?? null;
  const existingDatasourceId = build.existingDatasourceId ?? null;
  const base = {
    case: failureCase,
    googleAccount,
    onlyDashAccount,
    existingDatasourceId,
    driveFileId: build.driveFileId ?? null,
    driveFileName: build.driveFileName ?? null,
    folderId: build.folderId ?? null,
  };

  switch (failureCase) {
    case "invalid_link":
      return {
        ...base,
        title: "That is not a Google Sheet link",
        explanation:
          "OnlyDash reads Google Sheets. Copy the link from the sheet's address bar while the sheet is open. An .xlsx file in Drive is not a Google Sheet.",
        fix: "Paste a link that starts with https://docs.google.com/spreadsheets/d/",
        actions: ["edit_link"],
      };
    // MOSA-408. One case per link type. The user pasted something real; what
    // they need is the one step that turns it into a sheet OnlyDash can read.
    case "google_doc":
      return {
        ...base,
        title: "That is a Google Doc, not a Google Sheet",
        explanation:
          "OnlyDash builds a panel from rows and columns. A document has neither, so there is nothing to put in a table.",
        fix: "Paste the link to a Google Sheet instead.",
        actions: ["edit_link"],
      };
    case "google_form":
      return {
        ...base,
        title: "That is a Google Form, not a Google Sheet",
        explanation:
          "A form collects answers. The answers live in a separate spreadsheet, and that spreadsheet is what OnlyDash reads.",
        fix: "In the form, open Responses, then Link to Sheets. Open that sheet and paste its link here.",
        actions: ["edit_link"],
      };
    case "google_slides":
      return {
        ...base,
        title: "That is a Google Slides file, not a Google Sheet",
        explanation:
          "OnlyDash builds a panel from rows and columns. A presentation has neither.",
        fix: "Paste the link to a Google Sheet instead.",
        actions: ["edit_link"],
      };
    case "drive_folder":
      return {
        ...base,
        title: "That is a Drive folder",
        explanation:
          "A folder can hold many files. OnlyDash connects one sheet at a time, so it cannot tell which one you mean.",
        fix: "Choose one sheet in the folder.",
        actions: ["pick_in_folder", "edit_link"],
      };
    case "published_copy":
      return {
        ...base,
        title: "That is a published copy of a sheet",
        explanation:
          "A published link shows a read-only web page. It is not the sheet, so OnlyDash cannot read the rows or write back.",
        fix: "Open the sheet itself, then copy the link from the address bar.",
        actions: ["edit_link"],
      };
    case "excel_file":
      return {
        ...base,
        title: build.driveFileName
          ? `"${build.driveFileName}" is an Excel file`
          : "That is an Excel file, not a Google Sheet",
        explanation:
          "OnlyDash reads Google Sheets. An .xlsx or .csv file in Drive keeps its own format until you convert it.",
        fix: build.driveFileId
          ? "Convert it to a Google Sheet. OnlyDash then reads the copy, and your original file stays as it is."
          : "Open the file in Google Sheets, choose File then Save as Google Sheets, and paste the new link.",
        actions: build.driveFileId
          ? ["convert_to_sheet", "edit_link"]
          : ["edit_link"],
      };
    case "read_only_panel":
      return {
        ...base,
        title: "This panel is read-only",
        explanation: googleAccount
          ? `${googleAccount} can view this sheet but not change it, so OnlyDash connected it read-only. Nothing was written.`
          : "The connected Google account can view this sheet but not change it, so OnlyDash connected it read-only. Nothing was written.",
        fix: "Ask the sheet owner for edit access, then connect Google again. Until then you can read and filter the rows.",
        actions: ["request_edit_access", "switch_account"],
      };
    case "not_signed_in":
      return {
        ...base,
        title: "Sign up to connect this sheet",
        explanation:
          "Your sheet link is saved. Connecting a sheet needs an OnlyDash account, so the link is still here when you come back.",
        fix: "Sign up, then the wizard continues from this step.",
        actions: ["sign_up", "sign_in"],
      };
    case "consent_denied":
      return {
        ...base,
        title: "Google sign-in did not finish",
        explanation:
          "OnlyDash needs your permission to read this one spreadsheet. It cannot see any other file in your Drive.",
        fix: "Choose Allow on the Google screen. Nothing else in your Drive is shared.",
        actions: ["try_again"],
      };
    case "no_access":
      return {
        ...base,
        title: googleAccount
          ? `${googleAccount} cannot open this sheet`
          : "This Google account cannot open this sheet",
        explanation: googleAccount
          ? `OnlyDash is connected to ${googleAccount}. The sheet is not shared with that account, so the read is refused.`
          : "The sheet is not shared with the connected Google account, so the read is refused.",
        fix: "Ask the sheet owner to share it with this Google account, or switch to the Google account that owns it.",
        actions: [
          "switch_account",
          "copy_request_message",
          "open_request_access",
          "try_again",
        ],
      };
    case "scope_missing":
      return {
        ...base,
        title: "Google did not give OnlyDash access to your files",
        explanation:
          "The Google screen has one box for Drive file access. The box was not ticked, so OnlyDash cannot read the sheet. OnlyDash did not save a connection.",
        fix: "Connect Google again. Tick the box for Drive file access on the Google screen, then choose Allow.",
        actions: ["try_again"],
      };
    case "admin_policy":
      return {
        ...base,
        title: googleAccount
          ? `Google Workspace blocks OnlyDash for ${googleAccount}`
          : "Google Workspace blocks OnlyDash",
        explanation:
          "A Google Workspace admin policy stops this app. You cannot change this setting yourself. Your Workspace admin must allow the app.",
        fix: "Send the request below to your Workspace admin. When the admin allows OnlyDash, connect Google again.",
        actions: ["copy_admin_request", "try_again"],
      };
    case "picker_blocked":
      return {
        ...base,
        title: "Google's file window did not open",
        explanation:
          "The browser blocked the Google file window. A popup blocker or a cookie rule can cause this. OnlyDash cannot see the cause from here.",
        fix: "Open the file window in a new tab. Allow popups and third-party cookies for this site, then try again.",
        actions: ["open_picker_new_tab", "open_picker"],
      };
    case "picker_not_granted":
      return {
        ...base,
        title: "The sheet was not granted",
        explanation:
          "The picker closed without giving access to this file. OnlyDash can only read the file you pick there.",
        fix: "Open the picker and select this sheet.",
        actions: ["open_picker"],
      };
    case "sheet_not_found":
      return {
        ...base,
        title: "This sheet does not exist",
        explanation:
          "The sheet was deleted, moved to the trash, or the link has a typo.",
        fix: "Restore the sheet in Google Drive, or paste the link again.",
        actions: ["edit_link", "try_again"],
      };
    case "temporary": {
      const seconds = retryAfterSeconds(error);
      return {
        ...base,
        title: "Google is busy",
        explanation: `Google stopped answering for a moment. This is temporary. The wizard tries again in ${seconds} seconds.`,
        fix: "Wait, or press Retry now.",
        actions: ["retry_later"],
        retryAfterSeconds: seconds,
      };
    }
    case "too_large":
      return {
        ...base,
        title: "This sheet is too large",
        explanation:
          "OnlyDash reads sheets up to 500,000 cells. This one is larger, so reading it would be slow and unreliable.",
        fix: "Split the data across tabs so each tab is smaller, or paste a link to a smaller sheet.",
        actions: ["edit_link"],
      };
    case "not_configured":
      return {
        ...base,
        title: "Google sign-in is not configured yet",
        explanation:
          "The Google connection is not set up on this server. Everything else in OnlyDash works.",
        fix: "Nothing for you to do. Your sheet link is kept for later.",
        actions: [],
      };
    case "wrong_file":
      return {
        ...base,
        title: "You picked a different sheet than the link",
        explanation:
          "The Google picker returned a different file than the link you pasted. OnlyDash reads only a file you pick.",
        fix: "Use the sheet from the link, or switch to the one you picked.",
        actions: ["pick_pasted_sheet", "use_picked_file"],
      };
    case "spreadsheet_mismatch":
      return {
        ...base,
        title: "You picked a different sheet than the link",
        explanation:
          "The sheet on this link is not the sheet you picked. That happens when the picker file differs from the pasted link.",
        fix: "Use the sheet from the link, or switch to the one you picked.",
        actions: ["pick_pasted_sheet", "use_picked_file"],
      };
    case "already_connected":
      return {
        ...base,
        title: "This sheet is already connected with another Google account",
        explanation:
          "This project already has this sheet under a different Google connection. OnlyDash does not create a second source.",
        fix: "Open the existing source, or switch Google account. The sheet is connected under another Google account.",
        actions: ["open_existing", "use_my_account"],
      };
    case "link_required":
      return {
        ...base,
        title: googleAccount
          ? `${googleAccount} already has an OnlyDash account`
          : "This email already has an OnlyDash account",
        explanation:
          "The account was made with a password. OnlyDash did not create a second account and did not change this one.",
        fix: "Sign in with your password once. OnlyDash then links Google to that account, and the next time one Google screen is enough.",
        actions: ["sign_in_to_link"],
      };
    case "analyse_timeout":
      return {
        ...base,
        title: "Building the panel took too long",
        explanation:
          "OnlyDash stopped waiting for the analysis. Your sheet is still connected.",
        fix: "Retry to generate the panel.",
        actions: ["try_again"],
      };
    case "unknown":
    default:
      return {
        ...base,
        title: "The sheet could not be read",
        explanation:
          "Something stopped the read before OnlyDash could see the sheet.",
        fix: "Try again. If it keeps failing, paste the link again.",
        actions: ["try_again", "edit_link"],
      };
  }
}

/** True when the wizard retries this on a timer without a click. */
export const isAutoRetry = (failure: SheetsFailure): boolean =>
  failure.case === "temporary" && typeof failure.retryAfterSeconds === "number";
